import { SupportedLocale } from '@99designs/i18n';

export type CloudinaryImage = {
    type: 'cloudinary';
    cloudinaryImagePath: string;
    aspectRatio: number;
};

export type ExternalImage = {
    type: 'external';
    url: string;
    aspectRatio: number;
};
export type ProductImage = CloudinaryImage | ExternalImage;

export const isCloudinaryImage = (image?: ProductImage): image is CloudinaryImage => {
    return image?.type === 'cloudinary';
};

export const isExternalImage = (image?: ProductImage): image is ExternalImage => {
    return image?.type === 'external';
};

export type ProductKey =
    | 'acrylicWeddingSigns'
    | 'basicKidsShortSleeveTShirts'
    | 'bellaCanvasUnisexShortSleeveJerseyTShirtDtg2go'
    | 'brand-identity-pack'
    | 'caBasicTShirts'
    | 'carDoorMagnets'
    | 'carharttMidweightHoodedSweatshirtNa'
    | 'carbonlessForms'
    | 'circleSelfInkingStamps'
    | 'circleStickers'
    | 'classicCottonToteBagSmall'
    | 'customSheetStickers'
    | 'customSizeVinylBanners'
    | 'customTissuePaper'
    | 'deluxeCottonTote'
    | 'displayMailers'
    | 'doorHangers'
    | 'doubleSidedBanners'
    | 'embossedGlossBusinessCards'
    | 'fabricBanners'
    | 'flags'
    | 'flatMenus'
    | 'flatPouches'
    | 'flyers'
    | 'foamBoardWeddingSigns'
    | 'foamBoards'
    | 'foilAccentBusinessCards'
    | 'foodPackagingLabels'
    | 'giftCertificate'
    | 'gildanHeavyCottonYouthTShirtNa'
    | 'gildanZipUpSweatshirt'
    | 'glossyBusinessCards'
    | 'hanesAuthenticYouthShortSleeveTShirtNa'
    | 'hangTags'
    | 'jerzeesPolo'
    | 'letterhead'
    | 'logoEssential'
    | 'loyaltyCardStamps'
    | 'magneticCalendars'
    | 'mailingServicesPostcard'
    | 'matteBusinessCards'
    | 'menus'
    | 'meshBanners'
    | 'naWeddingCelebrationInvitations'
    | 'naWrappingPaper'
    | 'notebooks'
    | 'notepads'
    | 'paperBags'
    | 'payPalCarDoorMagnetsNa'
    | 'pennantBanners'
    | 'plasticLabels'
    | 'poleBanners'
    | 'polyMailers'
    | 'portAuthorityLifestyleBrimHatNa'
    | 'postcards'
    | 'posters'
    | 'preInkedStamps'
    | 'presentationFolders'
    | 'premiumPlusBusinessCards'
    | 'productBoxes'
    | 'productLabels'
    | 'proLogoDesign'
    | 'rackCards'
    | 'raisedFoilBusinessCards'
    | 'rectangleStickers'
    | 'retractableBanners'
    | 'returnAddressLabels'
    | 'rollLabels'
    | 'roundedCornerBusinessCards'
    | 'selfInkingStamps'
    | 'shippingBoxes'
    | 'shippingEnvelopes'
    | 'standUpPouches'
    | 'standardBusinessCards'
    | 'standardEmbroideredHats'
    | 'stepAndRepeatBanners'
    | 'stickyNotebookNa'
    | 'stickerSingles'
    | 'tablecloths'
    | 'tuckTopboxes'
    | 'ultraCottonYouthTShirt'
    | 'vinylBanners'
    | 'vistaPrintPrintedBaseballCap'
    | 'vistaprintCuffedBeanie'
    | 'vistaprintPremiumLaptopBackpack15'
    | 'weddingEnclosureCards'
    | 'weddingInvitations'
    | 'weddingMenus'
    | 'weddingPrograms'
    | 'weddingRsvpCards'
    | 'weddingThankYouCards'
    | 'windowDecals'
    | 'xBanners'
    | 'invitationsAnnouncements'
    | 'qrCodeBusinessCards'
    | 'squareBusinessCards'
    | 'ultraThickBusinessCards'
    | 'plasticBusinessCards'
    | 'premiumMatteBusinessCards'
    | 'premiumGlossyBusinessCards'
    | 'uncoatedBusinessCards'
    | 'premiumRoundedCornerBusinessCards'
    | 'paintedEdgeBusinessCards'
    | 'loyaltyBusinessCards'
    | 'naturalTexturedBusinessCards'
    | 'durableBusinessCards'
    | 'premiumStandardShapeBusinessCards'
    | 'kraftBusinessCards'
    | 'bambooBusinessCards'
    | 'hempBusinessCards'
    | 'premiumSquareBusinessCards'
    | 'nfcBusinessCards'
    | 'premiumZipToteBags'
    | 'bulletNatural5ozCottonCanvasGroceryToteNa'
    | 'groceryToteWithAntimicrobialAdditiveNa'
    | 'koozieKampKoolerToteNa'
    | 'iglooDaytripperToteCoolerNa'
    | 'rainbowRecycledCottonConventionToteNa'
    | 'economyToteNa'
    | 'essentialZipConventionToteNa'
    | 'rainbowRecycledCottonGroceryToteNa'
    | 'reposeRecycledCottonBoxToteNa'
    | 'merchantCraftReviveRecycledToteCoolerNa'
    | 'packagingTape'
    | 'lockBottomBoxes'
    | 'yardSigns'
    | 'tableRunners'
    | 'tableTopSigns'
    | 'metalSigns'
    | 'wallPosters'
    | 'aFrames'
    | 'acrylicSigns'
    | 'plasticSigns'
    | 'tableTents'
    | 'pvcFreeBoradSigns'
    | 'plasticDoorSigns'
    | 'christmasCards'
    | 'noteCards'
    | 'thankYouCards'
    | 'customEnvelopes'
    | '4X6Postcards'
    | 'postItNotes'
    | 'embossedFoilPostcards'
    | '5X7Postcards'
    | 'roundedCornerPostcards'
    | '42X55Postcards'
    | '6X9Postcards'
    | '6X11Postcards'
    | 'businessCardStickers'
    | '55X85Postcards'
    | 'giftTags'
    | 'pocketStamps'
    | 'embossedGlossPostcards'
    | 'engravedBusinessCardHolder'
    | 'theInspirationLargeNotebookNa'
    | 'neonEdgeNotebookNa'
    | '5X7InchSnapElasticClosureNotebookNa'
    | 'customDieCutStickerSheets'
    | 'bumperStickers'
    | 'cosmeticLabels'
    | 'packagingLabels'
    | 'squareStickers'
    | 'wineLabels'
    | 'ovalStickers'
    | 'qRCodeStickers'
    | 'staticStickers'
    | 'beerLabels'
    | 'holographicStickers'
    | 'paperLabels'
    | 'frontAdhesiveStickers'
    | 'kidsLabels'
    | 'tattooStickers'
    | 'photoStickers'
    | 'partyStickers'
    | 'mensPremiumShortSleeveTShirts'
    | 'elevateMensDadeShortSleevePoloShirtNa'
    | 'gildanDryBlendJerseySportShirtNa'
    | 'premiumWomensShortSleeveTShirts'
    | 'gildanPerformanceLongSleeveShirtNa'
    | 'yupoongFivePanelClassicTruckerCapNa'
    | 'longSleeveMensTShirt'
    | 'gildanHeavyCottonWomensShortSleeveTShirtNa'
    | 'districtVeryImportantTeeVNeckWithFullFrontPrintNa'
    | 'jerzeesWomensPoloShirts'
    | 'gildanDryBlendLongSleeveTShirtNa'
    | 'gildanHeavyBlendAdultHoodedSweatshirtNa'
    | 'elevateMensTazaKnitQuarterZipNa'
    | 'carharttRainDefenderPaxtonHeavyweightHoodedSweatshirtNa'
    | 'portCompanyMensCoreCottonInkPrintedTShirtNa'
    | 'portAuthorityRibKnitCuffBeanie'
    | 'featherLiteValuePolyesterSportShirtNa'
    | 'anvil45OunceRingSpunInkPrintedLightweightMensTShirtNa'
    | 'portAuthorityLadiesSummitFleeceFullZipJacketNa'
    | 'legacyDashboardTruckerCapNa'
    | 'districtWomensVeryImportantTeeLongSleeveWithFullFrontAndBackScreenprintNa'
    | 'comfortColorsGarmentDyedHeavyweightRingspunLongSleeveTShirtNa'
    | 'independentTradingCoUnisexSpecialBlendRaglanHoodedFullZipSweatshirtNa'
    | 'districtVitMuscleTankWithLeftChestAndBackScreenprintNa'
    | 'sportTekSleevelessPerformanceTeeScreenprintNa'
    | 'core365MensFusionChromaSoftPiquePoloNa'
    | 'districtWomensVeryImportantTeeWithFullFrontScreenprintNa'
    | 'districtWomensVeryImportantTeeVNeckWithLeftChestPrintNa'
    | 'jerzeesNuBlendYouthHoodedSweatshirtNa'
    | 'portAuthorityTorrentWaterproofJacketNa'
    | 'allmadeWomensTriBlendVNeckTeeNa'
    | 'alternativeCottonJerseyGoToTeeNa'
    | 'districtVeryImportantTeeLongSleeveWithFullFrontScreenprintNa'
    | 'driDuckTrekVestNa'
    | 'sportTekYouthSportWickHoodedPulloverNa'
    | 'spyderPassageBeanieNa'
    | 'portAuthorityMicrofleeceVestNa'
    | 'pumaMensGtCrossfadePoloShirtNa'
    | 'portAuthorityLadiesDiamondHeatherFleeceFullZipJacketNa'
    | 'sportTekSportWickFlexFleece14ZipNa'
    | 'northEndLadiesRadarQuarterZipPerformanceLongSleeveTopNa'
    | 'districtMadeWomensPerfectTriLongSleeveHoodieNa'
    | 'roots73MensRiversideFullZipHoodieNa'
    | 'portCompanySweatshirtToteNa'
    | 'wickedMensWovenShirtNa'
    | 'womensEasyCareGinghamCheckShirtNa'
    | 'portCompanyBouncerPocketTeeNa'
    | 'packaginginsertcards'
    | 'eddieBauerLadiesFleeceVestNa'
    | 'columbiaMensBahamaIiLongSleeveShirtNa'
    | 'driDuckMensFieldLongSleeveShirtNa'
    | 'jerzeesNuBlendYouthCrewneckSweatshirtNa'
    | 'sportTekWomensRashguardTeeNa'
    | 'sportTekColorblockMicropiqueSportWickPoloNa'
    | 'portAuthorityLadiesCoreClassicPiquePoloNa'
    | 'portAuthorityRTekStretchFleeceHeadbandNa'
    | 'sportTekWomensTriumphCowlNeckPulloverNa'
    | 'portAuthoritySuperProOxfordShirtNa'
    | 'portAuthorityProCamouflageSeriesGarmentWashedCapNa'
    | 'callawayTonalWomensPoloShirtNa'
    | 'portCompanyLadiesLongSleeveFanFavoriteVNeckTee'
    | 'nikeGolfDriFitSwooshPerforatedCapNa'
    | 'portAuthorityLadiesDryZoneOttomanPoloNa'
    | 'districtWomensVeryImportantTeeLongSleeveVNeckWithLeftChestAndBackPrintNa'
    | 'sportsmanKnitScarfNa'
    | '2GolfBallsTeesInTubeNa'
    | 'underArmourMensUltimateTeamJacketNa'
    | 'stormCreekLadiesLightweightMicrofleece14ZipPulloverNa'
    | 'sportTekWomensDraftHoodieTankNa'
    | 'core365WomensCruiseTwoLayerFleeceBondedSoftShellVestNa'
    | 'theNorthFaceApexBarrierSoftShellJacketNa'
    | 'portAuthorityLadiesMicrofleeceJacketNa'
    | 'portAuthorityRTekStretchAntiPillFleeceBeanieCapNa'
    | 'core365WomensBalanceColorblockPerformancePiquePoloNa'
    | 'portCompanyYouthPulloverHoodedSweatshirtNa'
    | 'portCompanyCamoBeanieCapNa'
    | 'riversEndLadiesPerformanceEdgeShortSleevePoloNa'
    | 'nikeGolfLadiesTechBasicDriFitPoloNa'
    | 'golfTeesBallMarkerSetNa'
    | 'fullCustomPerformanceTankTop'
    | 'portAuthorityMensPerformanceFineJacquardPoloNa'
    | 'theNorthFaceLadiesRidgelineSoftShellVestNa'
    | 'portAuthorityReversibleChargerVestNa'
    | 'womensHudsonDenimShirtNa'
    | 'saveTheDateCards'
    | 'personalizedMugs'
    | 'bookmarks'
    | 'carDoorDecals'
    | 'businessCardMagnets'
    | 'refrigeratorMagnets'
    | 'customTickets'
    | 'windowClings'
    | 'canvasPrints'
    | 'carWindowDecals'
    | 'tensionFabricDisplays'
    | 'wallDecals'
    | 'popUpDisplays'
    | 'naNapkins'
    | 'postcardMagnets'
    | 'naAdultBirthdayInvitations'
    | 'windowPerforatedVinyl'
    | 'fleeceBlankets'
    | 'graduationInvitations'
    | 'framedPrints'
    | 'flagsFabricOnly'
    | 'customLicensePlates'
    | 'kidsBirthdayInvitations'
    | 'popUpDisplaysGraphicOnly'
    | 'pillows'
    | 'babyShowerInvitations'
    | 'canCoolers'
    | 'placemats'
    | 'saveTheDateMagnets'
    | 'floorStandups'
    | 'stainlessSteelWideMouthInsulatedBottle22Oz'
    | 'floorDecals'
    | 'photoCoasters'
    | 'laserEtchedStemlessWineGlass'
    | 'babyAnnouncements'
    | 'engravedPintGlass'
    | 'customCanvasBags'
    | 'engravedPenWithMetallicFinishBestForSimpleLogosNa'
    | 'naMilestoneBirthdayInvitations'
    | 'naPhotoBalloons'
    | 'kidsWaterBottle'
    | '16OzCustomPrintedTumblers'
    | 'aluminumWaterBottleWithCarabiner26Oz'
    | 'laserEtchedCanGlass'
    | 'jivePenNa'
    | 'naCottonDrawstringBags'
    | '26OzStainlessSteelWaterBottleNa'
    | 'woodPrints'
    | 'urbanDrawstringSportspackNa'
    | '22OzStainlessSteelTravelMugFullColorNa'
    | 'bicClicSticWhiteBarrelBlackInkNa'
    | 'koozieBasicCollapsibleBottleKoolerNa'
    | 'britebrandClassicBallpointPen'
    | 'portAuthorityUltraCoreCinchPackNa'
    | 'naReligiousBabyEventInvitations'
    | 'koozieZipUpBottleKoolerNa'
    | 'mmsSilverTinsWithPersonalizedLid15OzNa'
    | 'swell40OzThermalTravelerNa'
    | 'towelsByTmgCompetitorFitnessTowelNa'
    | 'towelsByTmgRivieraBeachTowelNa'
    | 'domedTinWithHouseShapedMintsCaseOf125Na'
    | 'oppoBluetoothHeadphonesWithMicNa'
    | 'coltonCopperVacuumInsulatedBottle20ozNa'
    | 'ultraSlimSoftyWithStylusBlackInkNa'
    | 'microCoralPlushBlanketNa'
    | 'velocityFlashDrive4GBNa'
    | 'pillowBoxes'
    | 'h2goLodgeThermalBottle169Oz'
    | 'h2goVentureThermalBottle40Oz'
    | 'portAuthorityTheOriginalPackNa'
    | 'individuallyWrappedFlavorBurstCrystalFruitCandiesCaseOf1000Na'
    | 'fullColorMugWithChocolateButtonsMugDrop11OzCaseOf36Na'
    | 'assortedJellyBelly1ozGoodyBagNa'
    | 'largeLaserEngravedBambooCuttingBoardWithSiliconeGripNa'
    | 'reposeRecycledCottonDrawstringBagNa'
    | 'zigooLaserEngravedSiliconeCollapsibleBottle18OzNa'
    | 'sharpieSGelWhiteBarrelPenNa'
    | 'gripWriteAmBlackInkPenWithAntimicrobialAdditiveNa'
    | 'wpPorterUtensilSet'
    | 'rectangleMultichargerNa'
    | '27OzH2goElevateEastmanTritanBottleNa'
    | 'manchesterPenWithBlueInkDigitalFullColorWrapNa'
    | 'manchesterPenWithBlackInkDigitalFullColorWrapNa'
    | 'executive15ComputerBackpackNa'
    | 'portAuthorityCamouflageColorblockSportDuffelNa'
    | 'championDrawstringCinchBackpackNa'
    | 'highSierraMagnumCompuBackpackNa'
    | 'theCapitol20DuffelNa'
    | '16OzAvaPlasticStadiumCupNa'
    | 'losAngelesPenWithBlackInkDigitalFullColorWrapNa'
    | 'engravedBottleOpenerKeychainNa'
    | 'vaultRfidSecurity15ComputerBackpackNa'
    | 'lilShorty17OunceAluminumSportsBottleNa'
    | 'aftRecycledDuffelNa'
    | 'goGoMobilePhoneStandScreenCleanerNa'
    | 'engineerMultiToolNa'
    | 'brewCopperVacuumInsulatedLaserEngravedMug12OzNa'
    | 'softTouchEvelynStylusPenWithBlackInkNa'
    | 'caseLogicEra14ComputerAttacheNa'
    | 'avianaPalmerDoubleWallBottle17OzNa'
    | 'rubberBandBallInCaseNa'
    | 'gourmetCubeWithCandyCornNa'
    | 'flashPowerBankNa'
    | 'towelsByTmgRivieraWhiteBeachTowelNa'
    | 'redBarrelPaperMateWriteBrothersStickPenNa'
    | 'signityRollerballSoftyBlueInkPenLaserEngravedNa'
    | 'souvenirHewPenNa'
    | 'souvenierTfwPenNa'
    | 'blackMarbleAndWoodLaserEngravedCoasterSetNa'
    | 'cullenMultiFunctionTapeMeasureNa'
    | 'goodValueDoubleLayerGolfTowelNa'
    | 'theHipsterDeluxeFannyPackNa'
    | 'mikeIkesSmallTopViewTinNa'
    | 'bigFoot20OzStainlessSteelVacuumTumblerNa'
    | 'samsoniteAndante222WheeledDuffelNa'
    | 'arcticZoneRepreveBackpackCoolerWithSlingNa'
    | 'roundOrnamentNa'
    | 'harritonAdultRestore49OzScrubBottomNa'
    | 'skyLightStylusPenWithBlackInkNa'
    | 'stainlessSteelTravelMug22OzNa'
    | 'andrewsJournalWithFullColorInkjetNa'
    | 'bikeWaterBottle20OzNa'
    | 'ecoFriendlyBambooArlingtonPenWithBlueInkNa'
    | 'sportTekWindPantScreenprintNa'
    | 'safeTravelsTraverseWalletNa'
    | 'highSierraImpactBackpackNa'
    | 'fullColorMineralHughesGelStylusPenWithBlueInkNa'
    | 'theTotalPackageToolSetNa'
    | 'executiveAlphaPenGiftSetWithBlueInkNa'
    | 'camelBakInsulatedBottleWithCupNa'
    | 'arcticZoneTitanThermalHpCopperLaserEngravedTumbler30OzNa'
    | 'goodValueTransparentDrySack25LNa'
    | 'highSierraFlyByCompuBackpackNa'
    | 'premiumFullColorPrintedBags'
    | 'customRibbon'
    | 'customPizzaBoxes'
    | 'customGableBox'
    | 'foldedHangtags'
    | 'customCoffeeSleeves'
    | 'soapBoxes'
    | 'squareMailer'
    | 'luxuryGiftBags'
    | 'autoLockBottomBoxes'
    | 'polytape'
    | 'chocolateBarBoxes'
    | 'tuckTopMailerBoxes'
    | 'plasticCups'
    | 'dieCutHandleBags'
    | 'envelopeSeals'
    | 'mailingLabels'
    | 'wineBags'
    | 'treatBags'
    | 'flyersandLeaflets'
    | 'customStickers'
    | 'mensTee'
    | 'spotUVBusinessCards'
    | 'magneticBusinessCards'
    | 'foilBusinessCards'
    | 'entryLevelBusinessCards'
    | 'mailingBoxes'
    | 'swingTags'
    | 'customPrintedTissuePaper'
    | 'promotionalBoxes'
    | 'paperCups'
    | 'paperBag'
    | 'foldedLeaflets'
    | 'magneticPostcards'
    | 'doorHanger'
    | 'lawnSigns'
    | 'aFrameSandwichBoards'
    | 'giftCertificates'
    | 'hardcoverNotebooks'
    | 'fabricStamps'
    | 'outdoorRetractableBanner'
    | 'waterproofSheetStickers'
    | 'syntheticRollLabels'
    | 'transparentLabels'
    | 'womensTee'
    | 'gildanMensPoloShirt'
    | 'gildanWomensPoloShirt'
    | 'printedCap'
    | 'basicKidsTShirts'
    | 'enclosureCards'
    | 'receptionCards'
    | 'stubbyHolder'
    | 'paperCoasters'
    | 'circleStamps'
    | 'nameTags'
    | 'saveTheDateMagnet'
    | 'rsvpCards'
    | 'r231fResultLadiesPrintableSoftshellJacket'
    | 'flagsNZ'
    | 'hoodie'
    | 'asColourWomensStapleTShirt'
    | 'autoLockBoxes'
    | 'basicKidsTShirtShortSleeve'
    | 'basicMensLongSleeveTShirt'
    | 'bcId003HoodedSweat'
    | 'businessEnvelopes'
    | 'cardboardSigns'
    | 'coasters'
    | 'consumerDrawstringBag'
    | 'cottoVerMensTShirt'
    | 'cottoVerWomensTShirt'
    | 'embroideredPolos'
    | 'euFabricBanners'
    | 'euMeshBanners'
    | 'euRectangleStickers'
    | 'euVinylBanners'
    | 'fastShippingBusinessCards'
    | 'flexiblePackagingDoypacks'
    | 'fliplidboxes'
    | 'foldedSwingTags'
    | 'foodLabels'
    | 'footballShirt'
    | 'fruitOfTheLoomBasicTShirt'
    | 'fruitOfTheLoomPremiumHoodedSweatJacket'
    | 'fruitOfTheLoomValueweightLongsleeveT'
    | 'fullCustomCyclingJersey'
    | 'fullCustomSportsBomberJacket'
    | 'fullCustomToteBag'
    | 'gbbPremiumBusinessCards'
    | 'gildanMensTShirt'
    | 'gildanWomensTShirt'
    | 'greenLineStamps'
    | 'handStamps'
    | 'iceCreamCup'
    | 'invoicePads'
    | 'journals'
    | 'kidsBasicTShirts'
    | 'kidsFootballShirts'
    | 'logoEssential'
    | 'loyaltyCardStamp'
    | 'loyaltyCards'
    | 'mailerBoxes'
    | 'metallicBusinessCards'
    | 'multiDecoVistaprintEmbroideredHats'
    | 'outdoorPoster'
    | 'papercup'
    | 'photoboothMagnets'
    | 'pillowBoxesEu'
    | 'pinBadges'
    | 'premiumAsColorMensTShirts'
    | 'promotionalBoxesEu'
    | 'pulloutBoxes'
    | 'pvcFoamBoard'
    | 'qRcodestickers'
    | 'raisedFoilSwingTags'
    | 'recycledpolymailers'
    | 'ringBinders'
    | 'signatureStamps'
    | 'singlePocketFolders'
    | 'softTouchBusinessCards'
    | 'spotUvPostcards'
    | 'textileStamps'
    | 'vistaprint6PanelBaseballHat'
    | 'vistaprintTwoToneZipCottonToteBag'
    | 'weddingFoamBoards'
    | 'weddingMenuCards'
    | 'wrappingPaper';

export interface BaseDesignProduct {
    key: ProductKey;
    title: string;
    availableLocales: SupportedLocale[];
    images: {
        preview?: ProductImage;
        searchPreview?: ProductImage;
    };
}

export type DesignProduct = BaseDesignProduct &
    (VistaDesignProduct | BifrostDesignProduct | NNDesignProduct);

export interface VistaDesignProduct extends BaseDesignProduct {
    type: 'vista';
    mpvId: string;
    urls: {
        getStarted: string;
        brief: string;
        productDetails?: string;
    };
}

export interface BifrostDesignProduct extends BaseDesignProduct {
    type: 'bifrost';
    mpvId: string;
    urls: {
        nnCategoryPage: string;
        productDetails: string;
    };
}

export interface NNDesignProduct extends BaseDesignProduct {
    type: 'nn';
    productId: string;
    urls: {
        nnCategoryPage: string;
    };
}

export const isVistaDesignProduct = (product?: DesignProduct): product is VistaDesignProduct => {
    return product?.type === 'vista';
};

export const isBifrostDesignProduct = (
    product?: DesignProduct
): product is BifrostDesignProduct => {
    return product?.type === 'bifrost';
};

export const isNNDesignProduct = (product?: DesignProduct): product is NNDesignProduct => {
    return product?.type === 'nn';
};

export type CloudinaryImageOptions = {
    width?: number;
};

export type ProductImageOptions = {
    cloudinaryOptions?: CloudinaryImageOptions;
};

export type DesignProductPresenter = DesignProduct & {
    isAvailableInLocale: (locale: SupportedLocale) => boolean;
    ctaUrl: (locale: SupportedLocale, crossPlatformTracking?: URLSearchParams | null) => string;
    translatedTitle: () => string;
    previewImage: (options?: ProductImageOptions) => { url: string; aspectRatio: number };
    searchPreviewImage: (options?: ProductImageOptions) => {
        url: string;
        aspectRatio: number;
    } | null;
};
